import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { ArrowLeftIcon, PlusIcon, XIcon, BuildingIcon, UserIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { AddressAutocomplete } from '../components/AddressAutocomplete'
import { contactHasName, normalizeContactFields, getContactDisplayName } from '../lib/leadHelpers'
import { resolveValidatedAssignedTo } from '../lib/leadConversionForm'
import { PAYMENT_METHODS, SALES_TAX_STATUSES } from '../components/property/types'
import { logActivity, ActivityTypes } from '../utils/activityLogger'
import { parseEntityNotes } from '../lib/entityNotes'
import { buildUpdateDescription, diffSnapshots } from '../lib/activityChangeSummary'

interface UserProfile {
  id: string
  first_name?: string
  last_name?: string
  email: string
}

interface Contact {
  id: string
  first_name: string
  last_name: string
  name?: string
  title?: string
  phone: string
  cell: string
  email: string
}

interface CustomerNote {
  timestamp: string
  note: string
}

export default function CustomerEdit() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [users, setUsers] = useState<UserProfile[]>([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'company' | 'contacts' | 'notes'>('company')
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error'
  } | null>(null)

  // Company Information
  const [companyData, setCompanyData] = useState({
    name: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    address_line_2: '',
    city: '',
    state: '',
    zip: '',
  })

  // Contacts
  const [contacts, setContacts] = useState<Contact[]>([
    { id: '1', first_name: '', last_name: '', title: '', phone: '', cell: '', email: '' },
  ])

  // Customer Management
  const [customerData, setCustomerData] = useState({
    status: 'active',
    assigned_to: '',
    payment_method: '',
    sales_tax_status: '',
  })

  const [notes, setNotes] = useState('')
  const [customerNotes, setCustomerNotes] = useState<CustomerNote[]>([])
  const [customFields, setCustomFields] = useState<Record<string, any>>({})
  const [newCustomFieldKey, setNewCustomFieldKey] = useState('')
  const [newCustomFieldValue, setNewCustomFieldValue] = useState('')
  const initialSnapshotRef = useRef<Record<string, unknown> | null>(null)

  const CUSTOMER_UPDATE_LABELS: Record<string, string> = {
    company_name: 'Company Name',
    phone: 'Phone',
    email: 'Email',
    website: 'Website',
    address: 'Address',
    city: 'City',
    state: 'State',
    zip: 'Zip',
    status: 'Status',
    assigned_to: 'Assigned To',
    payment_method: 'Default Payment Method',
    sales_tax_status: 'Default Sales Tax Status',
    contacts_count: 'Contacts',
    notes_count: 'Notes',
  }

  const buildCustomerSnapshot = (
    company: typeof companyData,
    management: typeof customerData,
    notesList: CustomerNote[],
    contactsList: Contact[]
  ): Record<string, unknown> => ({
    company_name: company.name,
    phone: company.phone,
    email: company.email,
    website: company.website,
    address: company.address,
    city: company.city,
    state: company.state,
    zip: company.zip,
    status: management.status,
    assigned_to: management.assigned_to || '',
    payment_method: management.payment_method || '(empty)',
    sales_tax_status: management.sales_tax_status || '(empty)',
    contacts_count: contactsList.filter((c) => contactHasName(c)).length,
    notes_count: notesList.length,
  })

  const withAssignedToLabel = (snapshot: Record<string, unknown>) => ({
    ...snapshot,
    assigned_to: resolveAssignedToLabel(String(snapshot.assigned_to || '')) || '(empty)',
  })

  const resolveAssignedToLabel = (assignedToId: string) => {
    if (!assignedToId) return ''
    const user = users.find((u) => u.id === assignedToId)
    if (!user) return assignedToId
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
  }

  useEffect(() => {
    fetchUsers()
    if (id) {
      fetchCustomer()
    }
  }, [id])

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email')
      if (error) throw error
      setUsers(data || [])
    } catch (error) {
      console.error('Error fetching users:', error)
    }
  }

  const fetchCustomer = async () => {
    try {
      const { data: customer, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', id)
        .single()

      if (customerError) throw customerError

      setCompanyData({
        name: customer.company_name || '',
        phone: customer.phone || '',
        email: customer.email || '',
        website: customer.website || '',
        address: customer.address || '',
        address_line_2: customer.address_line_2 || '',
        city: customer.city || '',
        state: customer.state || '',
        zip: customer.zip_code || '',
      })

      setCustomerData({
        status: customer.status || (customer.is_active ? 'active' : 'terminated'),
        assigned_to: customer.assigned_to || '',
        payment_method: customer.payment_method || '',
        sales_tax_status: customer.sales_tax_status || '',
      })

      // Load custom fields
      if (customer.custom_fields && typeof customer.custom_fields === 'object') {
        setCustomFields(customer.custom_fields)
      }

      // Fetch notes if available
      const parsedNotes = parseEntityNotes(customer.notes)
      setCustomerNotes(parsedNotes)

      initialSnapshotRef.current = buildCustomerSnapshot(
        {
          name: customer.company_name || '',
          phone: customer.phone || '',
          email: customer.email || '',
          website: customer.website || '',
          address: customer.address || '',
          address_line_2: customer.address_line_2 || '',
          city: customer.city || '',
          state: customer.state || '',
          zip: customer.zip_code || '',
        },
        {
          status: customer.status || (customer.is_active ? 'active' : 'terminated'),
          assigned_to: customer.assigned_to || '',
          payment_method: customer.payment_method || '',
          sales_tax_status: customer.sales_tax_status || '',
        },
        parsedNotes,
        customer.contacts && Array.isArray(customer.contacts) ? customer.contacts : []
      )

      // Fetch contacts if available
      if (customer.contacts && Array.isArray(customer.contacts) && customer.contacts.length > 0) {
        const formattedContacts = customer.contacts.map((c: any) => {
          const names = normalizeContactFields(c)
          return {
            id: c.id || Date.now().toString() + Math.random(),
            first_name: names.first_name,
            last_name: names.last_name,
            name: names.name,
            title: c.title || '',
            phone: c.phone || '',
            cell: c.cell || '',
            email: c.email || '',
          }
        })
        setContacts(formattedContacts)
      }
    } catch (error) {
      console.error('Error fetching customer:', error)
      setNotification({
        type: 'error',
        message: 'Error loading customer: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  const addContact = () => {
    const newContact: Contact = {
      id: Date.now().toString(),
      first_name: '',
      last_name: '',
      phone: '',
      cell: '',
      email: '',
    }
    setContacts([...contacts, newContact])
  }

  const removeContact = (contactId: string) => {
    setContacts(contacts.filter((c) => c.id !== contactId))
  }

  const updateContact = (contactId: string, field: string, value: string) => {
    setContacts(contacts.map((c) => (c.id === contactId ? { ...c, [field]: value } : c)))
  }

  const addNote = () => {
    if (notes.trim()) {
      const newNote: CustomerNote = {
        timestamp: new Date().toISOString(),
        note: notes.trim(),
      }
      setCustomerNotes([newNote, ...customerNotes])
      setNotes('')
    }
  }

  const removeNote = (timestamp: string) => {
    setCustomerNotes(customerNotes.filter((n) => n.timestamp !== timestamp))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const primaryContact = contacts[0] || null
      const primaryNames = primaryContact ? normalizeContactFields(primaryContact) : null

      const contactsData = contacts
        .filter((c) => contactHasName(c))
        .map((c) => {
          const names = normalizeContactFields(c)
          return {
            id: c.id,
            first_name: names.first_name,
            last_name: names.last_name,
            name: names.name,
            title: c.title || undefined,
            phone: c.phone,
            cell: c.cell,
            email: c.email,
          }
        })

      const notesToSave =
        notes.trim().length > 0
          ? [{ timestamp: new Date().toISOString(), note: notes.trim() }, ...customerNotes]
          : customerNotes

      // Update customer
      const assignedTo = await resolveValidatedAssignedTo(customerData.assigned_to)
      const { error: updateError } = await supabase
        .from('customers')
        .update({
          company_name: companyData.name || null,
          contact_first_name: primaryNames?.first_name || '',
          contact_last_name: primaryNames?.last_name || '',
          phone: companyData.phone || null,
          email: companyData.email || null,
          website: companyData.website || null,
          address: companyData.address || null,
          address_line_2: companyData.address_line_2 || null,
          city: companyData.city || null,
          state: companyData.state || null,
          zip_code: companyData.zip || null,
          status: customerData.status || 'onboarding',
          assigned_to: assignedTo,
          payment_method: customerData.payment_method || null,
          sales_tax_status: customerData.sales_tax_status || null,
          notes: notesToSave,
          custom_fields: Object.keys(customFields).length > 0 ? customFields : null,
          contacts: contactsData.length > 0 ? contactsData : null,
        })
        .eq('id', id)

      if (updateError) throw updateError

      const currentSnapshot = buildCustomerSnapshot(
        companyData,
        customerData,
        notesToSave,
        contacts
      )
      const changes = diffSnapshots(
        withAssignedToLabel(initialSnapshotRef.current || {}),
        withAssignedToLabel(currentSnapshot),
        CUSTOMER_UPDATE_LABELS
      )

      await logActivity({
        activity_type: ActivityTypes.CUSTOMER_UPDATED,
        entity_type: 'customer',
        entity_id: id!,
        description: buildUpdateDescription(`Customer "${companyData.name || 'Unnamed'}"`, changes),
        metadata: { changes },
      })

      initialSnapshotRef.current = currentSnapshot
      setCustomerNotes(notesToSave)
      setNotes('')

      setNotification({
        type: 'success',
        message: 'Customer updated successfully!',
      })
      setTimeout(() => {
        setNotification(null)
        navigate('/customers')
      }, 2000)
    } catch (error) {
      console.error('Error updating customer:', error)
      setNotification({
        type: 'error',
        message: 'Error updating customer: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setLoading(false)
    }
  }

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
        <MobilePageHeader
          sticky
          title="Edit Customer"
          onBack={() => navigate('/customers')}
          backLabel="Back to Customers"
        />

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tab Navigation */}
          <div className={mobile.surface}>
            <div className="sticky top-[3.25rem] z-10 flex overflow-x-auto scrollbar-none border-b border-gray-200 bg-white/95 backdrop-blur-md">
              <button
                type="button"
                onClick={() => setActiveTab('company')}
                className={`flex-1 min-w-[5.5rem] px-4 sm:px-6 min-h-[48px] flex items-center justify-center whitespace-nowrap text-sm font-medium transition-colors ${
                  activeTab === 'company'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Company
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('contacts')}
                className={`flex-1 min-w-[5.5rem] px-4 sm:px-6 min-h-[48px] flex items-center justify-center whitespace-nowrap text-sm font-medium transition-colors ${
                  activeTab === 'contacts'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Contacts
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('notes')}
                className={`flex-1 min-w-[5.5rem] px-4 sm:px-6 min-h-[48px] flex items-center justify-center whitespace-nowrap text-sm font-medium transition-colors ${
                  activeTab === 'notes'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Notes
              </button>
            </div>
            <div className="p-4 sm:p-6">
              {/* Company Tab */}
              {activeTab === 'company' && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <BuildingIcon className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Company Information</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={companyData.name}
                        onChange={(e) => setCompanyData({ ...companyData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        value={companyData.phone}
                        onChange={(e) => {
                          const formatted = e.target.value.replace(/\D/g, '').slice(0, 10)
                          const display =
                            formatted.length > 0
                              ? formatted.length <= 3
                                ? formatted
                                : formatted.length <= 6
                                ? `(${formatted.slice(0, 3)}) ${formatted.slice(3)}`
                                : `(${formatted.slice(0, 3)}) ${formatted.slice(3, 6)}-${formatted.slice(
                                    6
                                  )}`
                              : ''
                          setCompanyData({ ...companyData, phone: display })
                        }}
                        placeholder="(123) 456-7890"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={companyData.email}
                        onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Website
                      </label>
                      <input
                        type="text"
                        value={companyData.website}
                        onChange={(e) =>
                          setCompanyData({ ...companyData, website: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="www.example.com or example.com"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Address
                      </label>
                      <AddressAutocomplete
                        value={companyData.address}
                        onChange={(address) => setCompanyData({ ...companyData, address })}
                        onAddressSelect={(addressData) =>
                          setCompanyData({
                            ...companyData,
                            address: addressData.address,
                            city: addressData.city,
                            state: addressData.state,
                            zip: addressData.zip,
                          })
                        }
                        placeholder="Start typing address..."
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Address Line 2 <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        value={companyData.address_line_2}
                        onChange={(e) =>
                          setCompanyData({ ...companyData, address_line_2: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Office #, Suite, Unit, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={companyData.city}
                        onChange={(e) => setCompanyData({ ...companyData, city: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                      <input
                        type="text"
                        value={companyData.state}
                        onChange={(e) => setCompanyData({ ...companyData, state: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="NY"
                        maxLength={2}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Zip Code
                      </label>
                      <input
                        type="text"
                        value={companyData.zip}
                        onChange={(e) => setCompanyData({ ...companyData, zip: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="10001"
                      />
                    </div>
                  </div>

                  {/* Customer Management Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Management</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status
                        </label>
                        <select
                          value={customerData.status}
                          onChange={(e) =>
                            setCustomerData({ ...customerData, status: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="onboarding">Onboarding</option>
                          <option value="active">Active</option>
                          <option value="terminated">Terminated</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Assigned To
                        </label>
                        <select
                          value={customerData.assigned_to}
                          onChange={(e) =>
                            setCustomerData({ ...customerData, assigned_to: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Unassigned</option>
                          {users.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.first_name} {user.last_name} ({user.email})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Default Payment Method
                        </label>
                        <select
                          value={customerData.payment_method}
                          onChange={(e) =>
                            setCustomerData({ ...customerData, payment_method: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Not set</option>
                          {PAYMENT_METHODS.map((method) => (
                            <option key={method} value={method}>
                              {method}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Default Sales Tax Status
                        </label>
                        <select
                          value={customerData.sales_tax_status}
                          onChange={(e) =>
                            setCustomerData({ ...customerData, sales_tax_status: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Not set</option>
                          {SALES_TAX_STATUSES.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      Properties can use these defaults or override payment and tax settings per
                      building.
                    </p>
                  </div>

                  {/* Custom Fields Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Fields</h3>
                    <div className="space-y-3">
                      {Object.entries(customFields).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              {key}
                            </label>
                            <input
                              type="text"
                              value={value}
                              onChange={(e) =>
                                setCustomFields({ ...customFields, [key]: e.target.value })
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newFields = { ...customFields }
                              delete newFields[key]
                              setCustomFields(newFields)
                            }}
                            className="mt-6 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove field"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex items-end gap-2 pt-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Field Name
                          </label>
                          <input
                            type="text"
                            value={newCustomFieldKey}
                            onChange={(e) => setNewCustomFieldKey(e.target.value)}
                            placeholder="e.g., Industry, Size, etc."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Value
                          </label>
                          <input
                            type="text"
                            value={newCustomFieldValue}
                            onChange={(e) => setNewCustomFieldValue(e.target.value)}
                            placeholder="Field value"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (newCustomFieldKey.trim() && newCustomFieldValue.trim()) {
                              setCustomFields({
                                ...customFields,
                                [newCustomFieldKey.trim()]: newCustomFieldValue.trim(),
                              })
                              setNewCustomFieldKey('')
                              setNewCustomFieldValue('')
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Contacts Tab */}
              {activeTab === 'contacts' && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <UserIcon className="w-6 h-6 text-blue-600" />
                      <h2 className="text-xl font-semibold text-gray-900">Contact Information</h2>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {contacts.map((contact, index) => (
                      <div key={contact.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-medium text-gray-900">
                            {getContactDisplayName(contact)} {index === 0 && '(Primary)'}
                          </h3>
                          {contacts.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeContact(contact.id)}
                              className="p-1 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <XIcon className="w-5 h-5 text-red-600" />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              First Name {index === 0 ? '*' : ''}
                            </label>
                            <input
                              type="text"
                              required={index === 0}
                              value={contact.first_name}
                              onChange={(e) => updateContact(contact.id, 'first_name', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Last Name {index === 0 ? '*' : ''}
                            </label>
                            <input
                              type="text"
                              required={index === 0}
                              value={contact.last_name}
                              onChange={(e) => updateContact(contact.id, 'last_name', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Title
                            </label>
                            <input
                              type="text"
                              value={contact.title || ''}
                              onChange={(e) => updateContact(contact.id, 'title', e.target.value)}
                              placeholder="e.g., Manager, Director, etc."
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email
                            </label>
                            <input
                              type="email"
                              value={contact.email}
                              onChange={(e) => updateContact(contact.id, 'email', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Phone
                            </label>
                            <input
                              type="tel"
                              value={contact.phone}
                              onChange={(e) => {
                                const formatted = e.target.value.replace(/\D/g, '').slice(0, 10)
                                const display =
                                  formatted.length > 0
                                    ? formatted.length <= 3
                                      ? formatted
                                      : formatted.length <= 6
                                      ? `(${formatted.slice(0, 3)}) ${formatted.slice(3)}`
                                      : `(${formatted.slice(0, 3)}) ${formatted.slice(3, 6)}-${formatted.slice(
                                          6
                                        )}`
                                    : ''
                                updateContact(contact.id, 'phone', display)
                              }}
                              placeholder="(123) 456-7890"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Cell
                            </label>
                            <input
                              type="tel"
                              value={contact.cell}
                              onChange={(e) => {
                                const formatted = e.target.value.replace(/\D/g, '').slice(0, 10)
                                const display =
                                  formatted.length > 0
                                    ? formatted.length <= 3
                                      ? formatted
                                      : formatted.length <= 6
                                      ? `(${formatted.slice(0, 3)}) ${formatted.slice(3)}`
                                      : `(${formatted.slice(0, 3)}) ${formatted.slice(3, 6)}-${formatted.slice(
                                          6
                                        )}`
                                    : ''
                                updateContact(contact.id, 'cell', display)
                              }}
                              placeholder="(123) 456-7890"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addContact}
                      className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <PlusIcon className="w-5 h-5" />
                      Add Contact
                    </button>
                  </div>
                </div>
              )}

              {/* Notes Tab */}
              {activeTab === 'notes' && (
                <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Notes</h2>
                    <div className="flex gap-2 mb-4">
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) {
                            addNote()
                          }
                        }}
                        rows={3}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Add a note... (Ctrl+Enter to submit)"
                      />
                      <button
                        type="button"
                        onClick={addNote}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors self-start"
                      >
                        Add Note
                      </button>
                    </div>
                    <div className="space-y-3">
                      {customerNotes.length > 0 ? (
                        customerNotes.map((note) => (
                          <div key={note.timestamp} className="flex gap-3 bg-gray-50 rounded-lg p-3">
                            <div className="flex-1">
                              <p className="text-sm text-gray-700">{note.note}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(note.timestamp).toLocaleString()}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeNote(note.timestamp)}
                              className="p-1 hover:bg-red-50 rounded transition-colors"
                            >
                              <XIcon className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 italic">No notes yet</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:rounded-xl lg:border lg:border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/customers')}
              className={mobile.secondaryBtn}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={mobile.primaryBtn}
            >
              <PlusIcon className="w-5 h-5" />
              <span className="sm:hidden">{loading ? 'Saving...' : 'Save'}</span>
              <span className="hidden sm:inline">{loading ? 'Updating...' : 'Update Customer'}</span>
            </button>
          </div>
        </form>

        {/* Notification Popup Modal */}
        {notification && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div
              className={`bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl ${
                notification.type === 'success'
                  ? 'border-2 border-green-500'
                  : 'border-2 border-red-500'
              }`}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                    notification.type === 'success'
                      ? 'bg-green-100'
                      : 'bg-red-100'
                  }`}
                >
                  {notification.type === 'success' ? (
                    <svg
                      className="w-6 h-6 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-6 h-6 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <p
                    className={`text-lg font-medium ${
                      notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                    }`}
                  >
                    {notification.message}
                  </p>
                </div>
                <button
                  onClick={() => setNotification(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
    </MobilePage>
  )
}
